import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

import { useOrderDocumentsByOrderIdQuery } from 'api/calc'
import { COLORS } from 'config/constants'
import { useGetCalcIdFromRoute } from 'utils/hooks'

import './invoice-source-documents.scss'

const docKindLabelKey: Record<CalculationDocumentDocKind, string> = {
  invoice: 'calc__text__documentImport__typeInvoice',
  packing: 'calc__text__documentImport__typePacking',
  unknown: 'calc__text__documentImport__typeUnknown',
}

type Props = {
  invoiceId?: number
}

const InvoiceSourceDocuments = ({ invoiceId }: Props) => {
  const { t } = useTranslation()
  const orderId = useGetCalcIdFromRoute() || ''
  const { data: documents } = useOrderDocumentsByOrderIdQuery(orderId, { skip: !orderId })

  const invoiceDocuments = useMemo(
    () => (documents || []).filter(
      (document) => document.applied_at && document.applied_invoice_id === invoiceId,
    ),
    [ documents, invoiceId ],
  )

  if (!invoiceId || !invoiceDocuments.length) {
    return null
  }

  return (
    <div className='invoice-source-files'>
      <Typography component='span' variant='caption' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY }}>
        {t('calc__text__documentImport__sourceFilesTitle')}:
      </Typography>
      <ul className='invoice-source-files__list'>
        {invoiceDocuments.map((document) => {
          const fileTitle = document.file?.title || t('calc__text__documentImport__unnamedFile')
          const fileUrl = document.file?.file
          const kindKey = docKindLabelKey[document.doc_kind] || docKindLabelKey.unknown

          return (
            <li key={document.id} className='invoice-source-files__item'>
              {fileUrl ? (
                <Link
                  href={fileUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='hover'
                  variant='caption'
                >
                  {fileTitle}
                </Link>
              ) : (
                <Typography component='span' variant='caption'>
                  {fileTitle}
                </Typography>
              )}
              <Typography component='span' variant='caption' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY }}>
                {' '}({t(kindKey)})
              </Typography>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default InvoiceSourceDocuments
